// localAgentService.js
// Business logic layer for Local Agent context extraction and data-aware analysis

import {
    MODULE_FPM,
    MODULE_RISK_REGISTER,
    MODULE_KPI,
    MODULE_CAPABILITY_CELL,
    MODULE_KPI_ASSISTANT,
    MODULE_KPI_SETUP,
    MODULE_RISK_FULL_ANALYSIS,
    getScenariosByModule
} from './localAgentConstants';
import api from '../DataManagement/Api';
import config from '../../config';

/**
 * Parses the raw React component state contexts and produces standard flat structures.
 */
export const buildFpmPayload = (ctx) => {
    if (!ctx) return { capabilities: [], projectName: 'FPM Heatmap' };

    const flatCapabilities = [];
    const heatmapData = ctx.heatmapData || {};
    const functions = ctx.functions || [];
    const phases = ctx.phases || [];

    // processData format: processData['function' + functionID]['phase' + phaseID] = Array of process items
    Object.keys(heatmapData).forEach(funcKey => {
        const funcId = funcKey.replace('function', '');
        const funcObj = functions.find(f =>
            String(f.DecompositionFunctionProjectID) === funcId ||
            String(f.DecompositionFunctionId) === funcId ||
            String(f.FunctionId) === funcId
        );
        const funcName = funcObj ? (funcObj.FunctionName || funcObj.FunctionTitle || funcObj.Title) : `Function ${funcId}`;

        const phasesObj = heatmapData[funcKey] || {};
        Object.keys(phasesObj).forEach(phaseKey => {
            const phaseId = phaseKey.replace('phase', '');
            const phaseObj = phases.find(p =>
                String(p.DecompositionPhaseProjectID) === phaseId ||
                String(p.DecompositionPhaseId) === phaseId ||
                String(p.PhaseId) === phaseId
            );
            const phaseName = phaseObj ? (phaseObj.PhaseName || phaseObj.PhaseTitle || phaseObj.Title) : `Phase ${phaseId}`;

            const items = phasesObj[phaseKey] || [];
            if (Array.isArray(items)) {
                items.forEach(item => {
                    if (item && item.ProcessLevel1Name) {
                        const scoreVal = item.AggrScore ? parseFloat(item.AggrScore) : null;
                        let severity = 'high';
                        if (scoreVal !== null) {
                            if (scoreVal < 2.0) severity = 'severe';
                            else if (scoreVal < 3.0) severity = 'high';
                            else if (scoreVal < 4.0) severity = 'medium';
                            else severity = 'low';
                        }
                        flatCapabilities.push({
                            id: item.DecompositionProcessLevel1ID || item.ProcessLevel1ID,
                            name: item.ProcessLevel1Name,
                            function: funcName,
                            phase: phaseName,
                            score: scoreVal,
                            status: item.Status,
                            severity: severity
                        });
                    }
                });
            }
        });
    });

    return {
        projectId: ctx.projectId,
        // projectName: ctx.projectName || 'Mining Corp FPM',
        capabilities: flatCapabilities,
        modelingMode: ctx.modelingMode,
        rawContext: ctx // Passing the full raw API responses (heatmapData, functions, phases) to the agent API
    };
};

export const buildRiskRegisterPayload = (ctx) => {
    if (!ctx) return { capabilityName: 'Standard Capability', existingRootCause: '', existingSolution: '', existingKpis: [] };

    const editModal = ctx.editModal || {};
    const overviewData = ctx.overviewData || [];

    // Find active capability name based on decomposition ID if available
    let capabilityName = 'Selected Capability';
    if (editModal.decompositionId && Array.isArray(overviewData)) {
        const matchingCap = overviewData.find(item =>
            String(item.DecompositionProcessLevel1ID || item.ProcessLevel1ID || item.id) === String(editModal.decompositionId)
        );
        if (matchingCap) {
            capabilityName = matchingCap.ProcessLevel1Name || matchingCap.name || capabilityName;
        }
    } else if (editModal.capabilityName) {
        capabilityName = editModal.capabilityName;
    }

    return {
        projectId: ctx.projectId,
        decompositionId: editModal.decompositionId,
        capabilityName: capabilityName,
        existingRootCause: editModal.rootCause || '',
        existingSolution: editModal.solution || '',
        existingKpis: editModal.kpis || [],
        overviewData: overviewData,
        rawContext: ctx // Passing the full raw context state to the agent API
    };
};

export const buildKpiPayload = (ctx) => {
    if (!ctx) return { kpiTitle: 'Key Performance Indicator', metric: '' };

    return {
        kpiTitle: ctx.kpiTitle || ctx.KPITitle || 'Standard KPI',
        metric: ctx.metric || ctx.Metrics || '',
        baseline: ctx.baseline || ctx.Baseline || '',
        target: ctx.target || ctx.Target || '',
        targetDate: ctx.targetDate || ctx.TargetDate || '',
        estimatedSavings: ctx.estimatedSavings || ctx.EstimatedSavings || '',
        stakeholders: ctx.stakeholders || ctx.Stakeholders || '',
    };
};

export const buildCapabilityCellPayload = (ctx) => {
    if (!ctx) return { capabilityName: 'Unknown', score: 0 };

    let capabilityName = ctx.capabilityName || ctx.ProcessLevel1Name || 'Selected Capability';
    if (!ctx.capabilityName && !ctx.ProcessLevel1Name && ctx.processes && ctx.processes.length > 0) {
        capabilityName = ctx.processes[0].text || capabilityName;
    }

    return {
        capabilityName: capabilityName,
        functionName: ctx.functionName || '',
        phaseName: ctx.phaseName || '',
        score: ctx.score || ctx.AggrScore || 0,
        status: ctx.status || ctx.Status || '1',
        projectId: ctx.projectId,
        processId: ctx.processId,
        processes: ctx.processes || [],
        mechanisms: ctx.mechanisms || [],
        connectedProcesses: ctx.connectedProcesses || [],
        rawContext: ctx
    };
};

export const buildKpiAssistantPayload = (ctx) => {
    if (!ctx) return { capabilityName: 'Unknown', kpis: [], rootCause: '' };

    return {
        capabilityName: ctx.Capability || ctx.capabilityName || 'Selected Capability',
        rootCause: ctx.RootCause || '',
        solution: ctx.Solution || '',
        kpis: Array.isArray(ctx.KPIs) ? ctx.KPIs : [],
        rawContext: ctx
    };
};

/**
 * Primary analyzer endpoint. Returns a structured, data-aware static response in Phase 1.
 */
export const runLocalAgentAnalysis = async (module, scenarioId, payload) => {
    const timestamp = new Date().toISOString();

    try {
        const scenarios = getScenariosByModule(module);
        const scenarioObj = scenarios.find(s => s.id === scenarioId);

        // We send the actual text of the scenario prompt to the API, as well as the ID
        const promptText = scenarioObj
            ? `${scenarioObj.label} - ${scenarioObj.description}`
            : scenarioId;

        const requestBody = {
            module: module,
            scenarioId: scenarioId,
            promptText: promptText,
            contextData: payload
        };

        const response = await api.post(`${config.REACT_APP_AGENTIC_AI_URL}api/local-agent/analyze`, requestBody, {
            headers: {
                'Content-Type': 'application/json',
                'X-API-KEY': config.REACT_APP_AGENTIC_AI_KEY
            },
            timeout: 120000
        });

        // Handle both raw Axios response objects and intercepted unwrapped data objects
        const responseData = response.data ? response.data : response;

        if (responseData && (responseData.data || responseData.analysis)) {
            return {
                success: true,
                scenario: scenarioId,
                timestamp,
                analysis: responseData.data || responseData.analysis,
                evidence: responseData.evidence
            };
        }

        console.error("Local Agent unhandled response structure:", response);
        throw new Error('Invalid response structure from Agentic AI.');
    } catch (error) {
        console.error("Agentic AI API call failed:", error);
        throw error;

    }
};
